
import Vue from 'vue';
import { getBrandConfig } from '@/services/brandConfigService';
import { Category } from '@/config/types';
import { getLauncherUrl, LOADING_PAGE_HTML } from '@/services/gameLauncher';
import OWLaunchModal from '@/components/OWLaunchModal.vue';

const LANG_SELECT_BRANDS: string[] = ['entain'];

interface GameFile {
  name: string;
  type: string;
  action: string;
  actionType: string;
  url?: string;
  gameId?: string;
  launchProvider?: string;
  openDirectly?: boolean;
}

interface SelectedGame {
  name: string;
  gameType?: string;
  banner?: string;
  subtitle?: string;
  gameFiles?: GameFile[];
}

export default Vue.extend({
  name: 'MainBanner',
  components: { OWLaunchModal },
  props: {
    selectedGame: {
      type: Object as () => SelectedGame | null,
      default: null
    },
    selectedCategory: {
      type: String,
      default: null
    },
    currentBrandId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      currentBannerIndex: 0,
      rotationInterval: null as any,
      isLaunching: false,
      showLangModal: false,
      pendingLangFile: null as GameFile | null,
      showOWModal: false,
      pendingOWFile: null as GameFile | null,
    };
  },
  computed: {
    brandConfig() {
      return getBrandConfig(this.currentBrandId);
    },
    categoryBanners(): Record<string, string> {
      const banners: Record<string, string> = {};
      this.brandConfig.categories.forEach((cat: Category) => {
        banners[cat.name] = cat.banner;
      });
      return banners;
    },
    defaultBanners(): string[] {
      const configured = this.brandConfig.defaultBanners;
      if (configured && configured.length > 0) return configured;
      return this.brandConfig.categories
        .filter((cat: Category) => !cat.disabled)
        .map((cat: Category) => cat.banner);
    },
    categoryTitles(): string[] {
      return this.brandConfig.categories
        .filter((cat: Category) => !cat.disabled)
        .map((cat: Category) => cat.name);
    },
    bannerImage(): string {
      // 1. If a specific game is selected, use the game's banner
      if (this.selectedGame && this.selectedGame.banner) {
        const bannerPath = this.selectedGame.banner;
        if (bannerPath.startsWith('http://') || bannerPath.startsWith('https://')) return bannerPath;
        try {
          return require(`@/assets/${bannerPath}`);
        } catch (e) {
          if (bannerPath.endsWith('.png')) {
            try {
              return require(`@/assets/${bannerPath.replace('.png', '.jpg')}`);
            } catch (e2) {}
          } else if (bannerPath.endsWith('.jpg')) {
            try {
              return require(`@/assets/${bannerPath.replace('.jpg', '.png')}`);
            } catch (e2) {}
          }
          console.warn(`Banner not found: ${bannerPath}`);
          return require('@/assets/banners/mainBanner.png');
        }
      }
      
      // 2. If a category is selected, use the category's banner
      if (this.selectedCategory && this.categoryBanners[this.selectedCategory]) {
        try {
          return require(`@/assets/${this.categoryBanners[this.selectedCategory]}`);
        } catch (e) {
          console.warn(`Category banner not found: ${this.categoryBanners[this.selectedCategory]}`);
        }
      }
      
      // 3. Default state - rotating banners
      try {
        return require(`@/assets/${this.defaultBanners[this.currentBannerIndex]}`);
      } catch (e) {
        console.warn(`Rotating banner not found: ${this.defaultBanners[this.currentBannerIndex]}`);
        return require('@/assets/banners/mainBanner.png');
      }
    },
    gameTitle(): string {
      // 1. If a specific game is selected, use the game's name
      if (this.selectedGame && this.selectedGame.name) {
        return this.selectedGame.name;
      }
      
      // 2. If a category is selected, use the category's name
      if (this.selectedCategory) {
        return this.selectedCategory;
      }
      
      // 3. Default state: title rotation follows the banners
      const title = this.categoryTitles[this.currentBannerIndex];
      return title;
    }
  },
  methods: {
    async doLaunch(file: GameFile, lang?: string, currency?: string): Promise<void> {
      this.isLaunching = true;
      if (file.openDirectly || !file.gameId) {
        const directWin = window.open(file.url!, '_blank');
        if (!directWin) this.$emit('launch-error', 'Please allow popups for this site to launch games.');
        this.isLaunching = false;
        return;
      }
      const newWin = window.open('about:blank', '_blank');
      if (newWin) {
        newWin.document.write(LOADING_PAGE_HTML);
        newWin.document.close();
      }
      try {
        const url = await getLauncherUrl(file.gameId, file.launchProvider, file.url, lang, currency);
        if (newWin && !newWin.closed) {
          newWin.location.href = url;
        } else if (!newWin) {
          this.$emit('launch-error', 'Please allow popups for this site to launch games.');
        }
      } catch (e: any) {
        if (newWin && !newWin.closed) newWin.close();
        this.$emit('launch-error', e?.message || 'Game unavailable');
      } finally {
        this.isLaunching = false;
      }
    },

    cancelOWLaunch() {
      this.showOWModal = false;
      this.pendingOWFile = null;
    },

    async handleFileAction(file: GameFile) {
      if (file.actionType === 'disabled' || file.actionType === 'coming-soon') return;

      if (file.actionType === 'api-launch' && (file.gameId || file.url)) {
        if (file.launchProvider === 'oddswork') {
          this.pendingOWFile = file;
          this.showOWModal = true;
          return;
        }
        if (LANG_SELECT_BRANDS.includes(this.currentBrandId)) {
          this.pendingLangFile = file;
          this.showLangModal = true;
          return;
        }
        await this.doLaunch(file);
        return;
      }

      if (file.actionType === 'button' && file.url) {
        if (!file.url.startsWith('http')) {
          const cdnBase = (process.env.VUE_APP_CDN_URL || '/').replace(/\/$/, '');
          const relativePath = file.url.startsWith('/') ? file.url.slice(1) : file.url;
          const link = document.createElement('a');
          link.href = `${cdnBase}/${relativePath}`;
          link.download = '';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          window.open(file.url, '_blank');
        }
        return;
      }

      if (file.actionType === 'download' && file.url) {
        const link = document.createElement('a');
        if (file.url.startsWith('http')) {
          link.href = file.url;
        } else {
          const cdnBase = (process.env.VUE_APP_CDN_URL || '/').replace(/\/$/, '');
          const relativePath = file.url.startsWith('/') ? file.url.slice(1) : file.url;
          link.href = `${cdnBase}/${relativePath}`;
        }
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    },

    async confirmLang(lang: 'en' | 'pt') {
      this.showLangModal = false;
      const file = this.pendingLangFile;
      this.pendingLangFile = null;
      if (!file) return;

      const resolvedLang = lang === 'en' ? 'en-us' : 'pt-BR';
      const resolvedCurrency = lang === 'en' ? 'USD' : 'BRL';
      await this.doLaunch(file, resolvedLang, resolvedCurrency);
    },

    goBack() {
      this.$emit('go-back');
    },
    
    goToSlide(index: number) {
      this.currentBannerIndex = index;
      this.resetRotationTimer();
    },
    
    resetRotationTimer() {
      this.stopBannerRotation();
      this.startBannerRotation();
    },
    
    startBannerRotation() {
      
      // Only start rotation if no game or category is selected
      if (!this.selectedGame && !this.selectedCategory) {
        this.rotationInterval = setInterval(() => {
          this.currentBannerIndex = (this.currentBannerIndex + 1) % this.defaultBanners.length;
        }, 5000);
      } else {
        this.currentBannerIndex = 0;
      }
    },
    
    stopBannerRotation() {
      if (this.rotationInterval) {
        clearInterval(this.rotationInterval);
        this.rotationInterval = null;
      }
    }
  },
  
  mounted() {
    this.startBannerRotation();
  },

  beforeDestroy() {
    this.stopBannerRotation();
  },
  
  watch: {
    selectedGame() {
      this.stopBannerRotation();
      this.startBannerRotation();
    },
    
    selectedCategory() {
      this.stopBannerRotation();
      this.startBannerRotation();
    }
  }
});
